import type { AnalysisSnapshot, AnalysisTrend } from '../types/analysis'

export const DEMO_ORIGIN_TIMESTAMP = new Date('2026-08-30T09:00:00Z')

const trend = (id: string, label: string, points: number[], direction: AnalysisTrend['direction']): AnalysisTrend => ({
  id,
  label,
  current: points.at(-1) ?? 0,
  delta: (points.at(-1) ?? 0) - (points.at(-2) ?? 0),
  points,
  direction,
})

export const getAnalysisDemoSnapshot = ({ workspaceId }: { workspaceId: string }): AnalysisSnapshot => ({
  workspaceId,
  generatedAt: new Date(DEMO_ORIGIN_TIMESTAMP),
  health: {
    // Sample: healthy execution with watch-level blockers to make the demo actionable.
    score: 74,
    subScores: { execution: 82, blocker: 60, drift: 78, ops: 70, ai: 80 },
    state: 'WATCH',
  },
  work: {
    // Sample: busy agency workspace with an active delivery queue.
    total: 120,
    active: 45,
    completed: 60,
    overdue: 8,
    blocked: 5,
    stalled: 2,
    checklistTotal: 72,
    checklistCompleted: 44,
    byType: { GOAL: 12, TASK: 36, SUBTASK: 28, CHECKLIST: 44 },
  },
  team: {
    // Sample: stable named assignee load for the member mobile view.
    assigneeLoad: [
      { id: 'aria-founder', label: 'Aria Chen', total: 16, overdue: 1, completed: 8 },
      { id: 'marcus-wong', label: 'Marcus Wong', total: 13, overdue: 2, completed: 7 },
      { id: 'priya-shah', label: 'Priya Shah', total: 11, overdue: 1, completed: 6 },
      { id: 'diego-alvarez', label: 'Diego Alvarez', total: 9, overdue: 3, completed: 5 },
      { id: 'sam-patel', label: 'Sam Patel', total: 8, overdue: 1, completed: 4 },
    ],
    unassigned: 3,
  },
  ops: {
    // Sample: common operational dependencies across a live event plan.
    proofRequired: 7,
    budgeted: 31,
    stockTouched: 18,
    formsTouched: 9,
    integrationsTouched: 6,
  },
  governance: {
    // Sample: healthy recent review cadence with a small priority queue.
    recentlyUpdated: 24,
    highPriority: 6,
  },
  ai: {
    // Sample: visible AI assistance with a bounded follow-up queue.
    generatedCandidates: 14,
    lowConfidenceSignals: 3,
  },
  trends: [
    trend('completion', 'Completion', [38, 41, 43, 47, 48, 51, 54, 56, 58, 59, 60, 60], 'up-is-good'),
    trend('overdue', 'Overdue', [13, 12, 12, 11, 10, 11, 10, 9, 9, 8, 8, 8], 'up-is-bad'),
    trend('blocked', 'Blocked', [9, 9, 8, 8, 7, 7, 6, 6, 5, 5, 5, 5], 'up-is-bad'),
    trend('activity', 'Activity', [16, 18, 20, 19, 23, 25, 24, 27, 29, 31, 30, 32], 'up-is-good'),
    trend('proof', 'Proof', [12, 11, 11, 10, 10, 9, 9, 8, 8, 7, 7, 7], 'up-is-bad'),
    trend('forms', 'Forms', [3, 4, 4, 5, 5, 5, 6, 6, 7, 8, 8, 9], 'up-is-good'),
    trend('automation', 'Integrations', [2, 2, 3, 3, 4, 4, 4, 5, 5, 5, 6, 6], 'up-is-good'),
  ],
  blockerChains: [],
  driftEvents: [],
  readinessMatrix: [],
  recommendations: [],
})
